import React from 'react';
import { Link as v4Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import promise from 'promise';

// lodash
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import startCase from 'lodash/startCase';
import toUpper from 'lodash/toUpper';
import upperFirst from 'lodash/upperFirst';
import isNil from 'lodash/isNil';

// Constellation Components
import {
    Button,
    Checkbox,
    DialogBody,
    FormField,
    FormHelp,
    Heading,
    Input,
    Label,
    LabeledControl,
    Popper,
    Spacer,
    Text,
    Tooltip,
} from '@zillow/constellation';
import {
    HR,
    Link,
} from '@zillow/zrs-comet';

// actions
import AddressActions from 'app/shared/actions/AddressActions';
import ModalActions from 'app/shared/actions/ModalActions';
import UserActions from 'app/shared/actions/UserActions';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ListingActivationTerm from 'app/shared/components/ListingActivationTerms';
import pathsManifest from 'app/shared/pathsManifest';
import PostingActions from 'app/shared/actions/PostingActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import userUtils from 'app/shared/utils/userUtils';
import getLocationRestrictionErrorMessage from 'app/shared/utils/getLocationRestrictionErrorMessageUtils';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import StringConstants from 'app/shared/constants/StringConstants';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import TrackingEventsConstants from 'app/shared/constants/TrackingEventsConstants';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

// Selectors
import {
    AddressSelectors,
    ModalSelectors,
    PropertiesSelectors,
    RoutingSelectors,
    UserSelectors,
} from 'app/shared/selectors';

// service
import appLogger from 'app/shared/loggers/appLogger';

// utils
import contextUtils from 'app/shared/utils/contextUtils';

// sub-components
import ArchiveProperty from './components/ArchiveProperty';

// Styled Components
import {
    StyledAutoComplete,
    StyledCheckboxContainer,
    StyledModalDialog,
    StyledSelect,
} from './StyledComponents';

const logger = log.child({ name: 'react' });
const { RENTAL, ROOM } = PropertyConstants.LISTING_TYPES;
const {
    COMMUNITY,
    CONDO,
    HOUSE,
    TOWNHOUSE,
} = PropertyConstants.PROPERTY_TYPES;
const pathsOptions = pathsManifest.getAllPaths();

const CTA = {
    TITLE: {
        CREATE_MY_LISTING: 'Create my listing',
        BUILD_A_LEASES: 'Build a lease',
        MANAGE_MY_PROPERTY: 'Manage my property',
        SETUP_APPLICATIONS: 'Set up applications',
        SETUP_PAYMENTS: 'Set up payments',
    },
    GA_ACTION: {
        CREATE_LISTING_APPLICATIONS: 'CreateListing-Applications',
        CREATE_LISTING_LIST: 'CreateListing-List',
        CREATE_LISTING_LEASES: 'CreateListing-Leases',
        CREATE_LISTING_MANAGE: 'CreateListing-Manage',
        CREATE_LISTING_PAYMENTS: 'CreateListing-Payments',
    },
};

// messages that are not empty will display errors in the UI
const defaultErrorObj = {
    address: {
        message: '',
    },
    unitNumber: {
        message: '',
    },
    propertyType: {
        message: '',
    },
    numberOfUnits: {
        message: '',
    },
};

class AddPropertyModal extends React.Component {
    static contextType = RMWContext;

    static propTypes = {
        aliasEncoded: PropTypes.string,
        behavior: PropTypes.oneOf(['Add', 'Edit']),
        isOpen: PropTypes.bool,
        address: PropTypes.string,
        // once we hook up links from apps and payments,
        // pass destination prop from the parent to determine where to send users upon modal submit
        destinationTab: PropTypes.oneOf([
            pathsOptions.propertyDetailsListing,
            pathsOptions.propertyDetailsApplications,
            pathsOptions.propertyDetailsPayments,
            pathsOptions.propertyDetailsLeases,
        ]),
        listing: PropTypes.shape({
            propertyId: PropTypes.string,
            propertyType: PropTypes.string,
            street: PropTypes.string,
            unit: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zip: PropTypes.string,
            numUnits: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            isCommunity: PropTypes.bool,
        }),
        isRoomForRent: PropTypes.bool,
        unit: PropTypes.string,
        preventLandingPageRedirect: PropTypes.bool,
        skipEditStage: PropTypes.bool,
        currentProperty: PropTypes.object,
    };

    static defaultProps = {
        aliasEncoded: null,
        behavior: 'Add',
        destinationTab: pathsOptions.propertyDetailsListing,
        address: '',
        unit: undefined,
        isRoomForRent: false,
        preventLandingPageRedirect: false,
        skipEditStage: false,
        currentProperty: {},
        isOpen: false,
        listing: {},
    };

    constructor(props) {
        super(props);

        const {
            address,
            behavior,
            destinationTab,
            isOpen,
            isRoomForRent,
            search,
            unit,
        } = this.props;

        // init state
        this.state = {
            address,
            behavior,
            destinationTab,
            errorObj: defaultErrorObj,
            isOpen,
            numUnits: '',
            isRoomForRentChecked: isRoomForRent,
            mktPath: contextUtils.getMarketingPathVal(search) || null,
            propertyType: '',
            unit,
            disableSubmit: false,
            disableDeletePropertySubmit: true,
            deletePropertyReason: '',
            isUserAtArchiveStep: false,
            isAddressEditable: true,
            keyUpTimeoutForAddress: null,
        };

        this.modalClose = this.modalClose.bind(this);
        this.handleAddPropertySubmit = this.handleAddPropertySubmit.bind(this);
        this.handleAddressChange = this.handleAddressChange.bind(this);
        this.handleEditPropertySubmit = this.handleEditPropertySubmit.bind(this);
        this.handleDeletePropertySubmit = this.handleDeletePropertySubmit.bind(this);
        this.handleTriggerAction = this.handleTriggerAction.bind(this);
        this.selectAddress = this.selectAddress.bind(this);
        this.updateMFNumUnits = this.updateMFNumUnits.bind(this);
        this.updateDeletePropertyReason = this.updateDeletePropertyReason.bind(this);
        this.toggleRoomForRentChecked = this.toggleRoomForRentChecked.bind(this);
        this.updatePropertyType = this.updatePropertyType.bind(this);
        this.updateUnit = this.updateUnit.bind(this);
        this.validateAddress = this.validateAddress.bind(this);
        this.validateForm = this.validateForm.bind(this);
        this.validateIsEmptyForm = this.validateIsEmptyForm.bind(this);
        this.handleArchiveOrUnArchive = this.handleArchiveOrUnArchive.bind(this);
        this.abortArchiveProcess = this.abortArchiveProcess.bind(this);
        this.remapErrorMessage = this.remapErrorMessage.bind(this);
        this.renderAddPropertyModalContent = this.renderAddPropertyModalContent.bind(this);
        this.renderAddBehaviorCTAContainer = this.renderAddBehaviorCTAContainer.bind(this);
        this.renderSupportOrAddNewPropertyError = this.renderSupportOrAddNewPropertyError.bind(this);
        this.handleAddPropertyClick = this.handleAddPropertyClick.bind(this);
        this.handleUnitsChange = this.handleUnitsChange.bind(this);
    }

    // in order to persist updates to property type on save
    // propetyType is updated to currentProperty via PropertiesActions @savePropertyInfo
    // in this context the prop listing is currentProperty

    // TODO: refactor
    // remove propertyType from props and use listing.propetyType

    componentWillReceiveProps(nextProps) {
        const { address, isOpen } = this.state;

        // new instance of modal has opened
        // we have listing on nextProps in 'edit' behavior
        if (nextProps.listing && nextProps.listing.street) {
            const { listing, propertyType } = nextProps;
            const { skipEditStage } = this.props;

            this.setState({
                address,
                errorObj: defaultErrorObj,
                unit: listing ? listing.unit : undefined,
                // not editable but displayed
                propertyType: propertyType || '',
                numUnits: listing ? listing.numUnits : '',
                isRoomForRentChecked: nextProps.isRoomForRent,
                errorAtArchive: false,
                isUserAtArchiveStep: skipEditStage,
                isAddressEditable: listing.isAddressEditable,
            });
        }

        if (nextProps.isOpen !== isOpen) {
            const { currentProperty } = nextProps;
            const { isAddressValid } = currentProperty;
            let copyErrorObj = { ...defaultErrorObj };
            let fullAddress;
            let isAddressEditable;

            if (nextProps.behavior === 'Add') {
                fullAddress = '';
                isAddressEditable = true;
                if (nextProps.isOpen) {
                    this.props.dispatch(UserActions.trackEvent({
                        trackingEvent: TrackingEventsConstants.OPEN_ADD_PROPERTY_MODAL,
                    }));
                }
            } else {
                const isAddressEditableFromProps = nextProps.currentProperty.isAddressEditable;
                fullAddress = this.getFormattedAddress(nextProps.currentProperty);
                isAddressEditable = isAddressEditableFromProps;
            }

            // check if address is valid
            if (isAddressValid === false) {
                // address is not valid
                copyErrorObj = {
                    ...copyErrorObj,
                    address: {
                        message: 'Please provide a valid address',
                    },
                };
            }

            this.setState({
                address: fullAddress,
                errorObj: copyErrorObj,
                isOpen: nextProps.isOpen,
                behavior: nextProps.behavior,
                isAddressEditable,
            });
        }

        // check destination
        if (nextProps.destinationTab) {
            this.setState({ destinationTab: nextProps.destinationTab });
        }

        // FIXME: passing error as a prop does n ot work
        // for some reason I was getting an infinite setState loop
        // when trying to pass the error as a prop
        // check errors
        const { modalAddProperty } = this.props;
        if (!isEqual(nextProps.modalAddProperty.error, modalAddProperty.error)) {
            this.setState({ errorObj: nextProps.modalAddProperty.error });
        }
    }

    getFormattedAddress(listing) {
        const {
            street,
            city,
            state,
            zip,
        } = listing;
        const words = street.split(' ');
        const formattedStreet = map(words, upperFirst).join(' ');
        const cityState = `${startCase(city)}, ${toUpper(state)}`;

        return street.length === 0 ? '' : [`${formattedStreet},`, cityState, zip].filter(val => val).join(' ');
    }

    modalClose(createdListing = false, isModalDismissed = false) {
        const { brand, paths } = this.context;
        const {
            aliasEncoded,
            totalListings,
            preventLandingPageRedirect,
            dispatch,
        } = this.props;

        const { errorObj } = this.state;

        if (!preventLandingPageRedirect) {
            // if zero listings and didn't just create one, send back to landing page
            if (
                brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow &&
                totalListings === 0 &&
                !createdListing
            ) {
                window.router.transitionTo(paths.base);
            }
        }

        if (isModalDismissed) {
            const gaEvent = {
                ...AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_DISMISS,
                ...(aliasEncoded) && { label: aliasEncoded },
            };

            dispatch(analyticsUtils.track.GAEventV2(gaEvent));
        }
        dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: false }));
        const copyErrorObj = assign({}, errorObj, {
            address: {
                message: '',
            },
        });

        // close and clear out form
        this.setState({
            isOpen: false,
            address: '',
            unit: undefined,
            propertyType: '',
            numUnits: '',
            errorObj: copyErrorObj,
            isUserAtArchiveStep: false,
            errorAtArchive: false,
            isAddressEditable: true,
        });
    }

    handleAddressChange(value) {
        this.setState({
            disableSubmit: false,
            address: value,
        });
    }

    renderAddBehaviorCTAContainer() {
        const { disableSubmit, mktPath } = this.state;

        // dynamic post property create vars
        let primaryBtnTitle = '';
        let primaryGAction = '';
        let secondaryBtnTitle = '';
        let secondaryGAAction = '';

        switch (mktPath) {
            case StringConstants.MARKETING_PATH.APPLICATIONS:
                primaryBtnTitle = CTA.TITLE.SETUP_APPLICATIONS;
                primaryGAction = CTA.GA_ACTION.CREATE_LISTING_APPLICATIONS;

                secondaryBtnTitle = CTA.TITLE.CREATE_MY_LISTING;
                secondaryGAAction = CTA.GA_ACTION.CREATE_LISTING_LIST;
                break;
            case StringConstants.MARKETING_PATH.PAYMENTS:
                primaryBtnTitle = CTA.TITLE.SETUP_PAYMENTS;
                primaryGAction = CTA.GA_ACTION.CREATE_LISTING_PAYMENTS;

                secondaryBtnTitle = CTA.TITLE.CREATE_MY_LISTING;
                secondaryGAAction = CTA.GA_ACTION.CREATE_LISTING_LIST;
                break;
            case StringConstants.MARKETING_PATH.LEASES:
                primaryBtnTitle = CTA.TITLE.BUILD_A_LEASES;
                primaryGAction = CTA.GA_ACTION.CREATE_LISTING_LEASES;

                secondaryBtnTitle = CTA.TITLE.CREATE_MY_LISTING;
                secondaryGAAction = CTA.GA_ACTION.CREATE_LISTING_LIST;
                break;
            default:
                primaryBtnTitle = CTA.TITLE.CREATE_MY_LISTING;
                primaryGAction = CTA.GA_ACTION.CREATE_LISTING_LIST;

                secondaryBtnTitle = CTA.TITLE.MANAGE_MY_PROPERTY;
                secondaryGAAction = CTA.GA_ACTION.CREATE_LISTING_MANAGE;
                break;
        }

        const prefixTerms = `By clicking ${primaryBtnTitle} or ${secondaryBtnTitle}`;

        return (
            <div>
                <Button
                    marginY="xs"
                    fluid={true}
                    buttonType="primary"
                    onClick={() => this.handleAddPropertySubmit(primaryGAction)}
                    disabled={disableSubmit}
                >
                    {primaryBtnTitle}
                </Button>
                <React.Fragment>
                    <Button
                        marginY="xs"
                        fluid={true}
                        buttonType="secondary"
                        onClick={() => this.handleAddPropertySubmit(secondaryGAAction)}
                        disabled={disableSubmit}
                    >
                        {secondaryBtnTitle}
                    </Button>
                    <ListingActivationTerm
                        actionText={prefixTerms}
                        lowlight={true}
                    />
                </React.Fragment>
            </div>
        );
    }

    handleAddPropertySubmit(gaaction = '') {
        const {
            aliasEncoded,
            dispatch,
        } = this.props;

        this.setState({
            disableSubmit: true,
        });

        this.validateForm()
            .then(() => {
                const { paths } = this.context;

                const {
                    destinationTab,
                    isRoomForRentChecked,
                    propertyType,
                    address,
                    unit,
                    numUnits,
                    duplicateAccountListingId,
                } = this.state;

                const listingType = isRoomForRentChecked ? ROOM : RENTAL;
                let propertyId;

                // Duplicate listing exists. Redirect to existing listing rather than creating new one
                if (duplicateAccountListingId && listingType !== ROOM) {
                    const gaEvent = {
                        ...AnalyticsConstants.GA_EVENTS.EXISTING_LISTING_REDIRECT,
                        ...(aliasEncoded) && { label: aliasEncoded },
                    };

                    dispatch(analyticsUtils.track.GAEventV2(gaEvent));
                    window.router.transitionTo(
                        `${paths.propertiesPage}/${duplicateAccountListingId}/${destinationTab}`,
                    );
                    this.modalClose(true);
                    return;
                }

                const isCommunityType = propertyType === PropertyConstants.PROPERTY_TYPES.COMMUNITY;
                const formData = {
                    listingType,
                    propertyType,
                    fullAddress: address,
                    ...(!isCommunityType && { unitNumber: unit }),
                };

                dispatch(PostingActions.property.create(formData))
                    .then((result) => {
                        const { propertyId: propertyIdFromResults } = result;
                        propertyId = propertyIdFromResults;

                        // trigger tracking event
                        window.dataLayer.push({ event: 'RentalManager-AddProperty' });

                        // Before we transition to EditListingPage, update this particular listing with
                        // number of units in community, if required.
                        if (isCommunityType) {
                            const dataToSave = {
                                fieldName: 'numUnits',
                                value: numUnits,
                                propertyId,
                            };

                            dispatch(PostingActions.property.edit(dataToSave));
                        }

                        // close on success
                        this.modalClose(true);
                        this.setState({
                            disableSubmit: false,
                        });
                    })
                    .then(() => {
                        // dynamically set action
                        AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_CTA.action = gaaction;
                        const gaEvent = {
                            ...AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_CTA,
                            ...(aliasEncoded) && { label: aliasEncoded },
                        };

                        dispatch(analyticsUtils.track.GAEventV2(gaEvent));

                        if (gaaction === CTA.GA_ACTION.CREATE_LISTING_LIST) {
                            // create my listing clicked
                            // ?intent=listing - will hide sub nav in pdp
                            window.router.transitionTo(
                                `${paths.propertiesPage}/${propertyId}/listing?intent=listing`
                            );
                        } else {
                            let path = `${paths.propertiesPage}/${propertyId}/${destinationTab}`;
                            switch (destinationTab) {
                                case StringConstants.MARKETING_PATH.PAYMENTS:
                                    path = `${paths.base}/${destinationTab}/properties/${propertyId}`;
                                    break;
                                case StringConstants.MARKETING_PATH.LEASES:
                                    path = `${paths.base}/leasing/property/${propertyId}`;
                                    break;
                                default:
                                    // no case
                                    break;
                            }

                            // use updateLocation as the path is most likely outside of RMW
                            window.router.updateLocation(path);
                        }
                    })
                    .catch(() => {
                        const clientErrMsg = 'listingCreatedError';
                        dispatch(
                            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.HPPP_NEW_LISTING_ERROR, {
                                action: [clientErrMsg, formData.listingType, formData.propertyType].join('-'),
                            }),
                        );

                        this.setState({
                            disableSubmit: false,
                        });
                    });
            })
            .catch(() => {
                // form is not valid
                this.setState({
                    disableSubmit: false,
                });
            });
    }

    /*
     * renderCometFeatureError
     * @errorRMWModel - error data model
     *
     * Remap the error message if certain condition are met.
     */
    remapErrorMessage(errorRMWModel) {
        const { paths } = this.context;
        const { data, message } = errorRMWModel;

        const matchDuplicatePropertyError = 'Another property with that address already exists.';
        const matchCometFeatureError = 'Please contact customer support if there is an error with the address. You' +
            ' can also add a new property or unit.';

        let targetPath = '';

        switch (message.toLowerCase()) {
            // comet features enabled
            case matchCometFeatureError.toLowerCase():
                targetPath = `${paths.helpPage}`;

                return this.renderSupportOrAddNewPropertyError();

            // duplicate listing
            case matchDuplicatePropertyError.toLowerCase():
                targetPath = `${paths.base}/properties/${data.duplicateListingId}/listing`;

                return (
                    <React.Fragment>
                        Another property with that address already exists.
                        <Link
                            reactRouterLink={v4Link}
                            onClick={() => {
                                window.router.transitionTo(targetPath);
                                this.modalClose();
                            }}
                        >
                            &nbsp;View that property.
                        </Link>
                    </React.Fragment>
                );

            default:
                return message;
        }
    }

    handleAddPropertyClick() {
        const { brand } = this.context;
        const { aliasEncoded, dispatch } = this.props;

        this.showAddPropertyModal();

        const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
        analyticsUtils.setCustomDimension('RoomForRentToolTipAddProperty', dimension, brand);

        const gaEvent = {
            ...AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL,
            ...(aliasEncoded) && { label: aliasEncoded },
        };

        dispatch(analyticsUtils.track.GAEventV2(gaEvent));
    }

    handleEditPropertySubmit() {
        const { dispatch } = this.props;
        const { errorObj } = this.state;

        this.setState({
            disableSubmit: true,
        });

        this.validateForm()
            .then(() => {
                const {
                    address,
                    unit,
                    propertyType,
                    numUnits,
                } = this.state;
                const { listing, propertyId } = this.props;
                const { propertyTypeCode } = listing;

                const propertyTypeCodes = PropertyConstants.PROPERTY_CODES_BY_TYPE;
                const addressNeedsUpdate = this.getFormattedAddress(listing) !== address;
                const unitNeedsUpdate = listing.unit !== unit;
                const numUnitsNeedsUpdate = listing.numUnits !== numUnits;

                const changingPropertyTypeCode = propertyTypeCodes[propertyType];
                const propertyTypeNeedsUpdate = changingPropertyTypeCode !== propertyTypeCode;
                const isCommunityType = propertyType === COMMUNITY;

                if (addressNeedsUpdate || unitNeedsUpdate || propertyTypeNeedsUpdate || numUnitsNeedsUpdate) {
                    // format data here
                    const dataToSave = {
                        fullAddress: address,
                        ...(!isCommunityType && { unit }),
                        numUnits,
                        propertyId,
                        propertyTypeCode: changingPropertyTypeCode,
                    };
                    dispatch(PropertiesActions.savePropertyInfo(dataToSave))
                        .then(() => window.location.reload())
                        .catch((errorRMWModel) => {
                            // TODO: add error handling here
                            // TODO: also need validation/error handling for the modal's form fields
                            this.setState({
                                disableSubmit: false,
                            });

                            const copyErrorObj = assign({}, errorObj, {
                                address: {
                                    message: this.remapErrorMessage(errorRMWModel),
                                },
                            });

                            this.setState({
                                disableSubmit: true,
                                errorObj: copyErrorObj,
                            });
                        });
                } else {
                    // values are the same
                    this.modalClose();

                    this.setState({
                        disableSubmit: false,
                    });
                }
            })
            .catch(() => {
                this.setState({
                    disableSubmit: false,
                });
            });
    }

    handleDeletePropertySubmit() {
        const { paths } = this.context;

        const {
            listing,
            propertyId,
            dispatch,
        } = this.props;
        const { deletePropertyReason } = this.state;

        // eslint-disable-next-line no-alert
        if (window.confirm(`Delete property? ${listing.street} will no longer be one of your properties.`)) {
            this.setState({
                disableSubmit: true,
            });

            dispatch(PostingActions.property.delete(propertyId, deletePropertyReason)).then(() => {
                window.router.updateLocation(paths.propertiesPage);
            });
        } else {
            this.setState({
                disableSubmit: false,
            });
        }
    }

    handleTriggerAction() {
        const { dispatch } = this.props;
        const { address } = this.state;

        dispatch(AddressActions.autocomplete(address));
    }

    // handle archive or unarchive
    handleArchiveOrUnArchive() {
        const {
            aliasEncoded,
            isArchived,
            propertyId,
            dispatch,
        } = this.props;

        const { isUserAtArchiveStep } = this.state;

        if (!isUserAtArchiveStep) {
            this.setState({ isUserAtArchiveStep: true });
        } else if (isArchived) {
            dispatch(PostingActions.property.unArchive(propertyId))
                .then((result) => {
                    if (result.success) {
                        const gaEvent = {
                            ...AnalyticsConstants.GA_EVENTS.UNARCHIVE_LISTING,
                            ...(aliasEncoded) && { label: aliasEncoded },
                        };

                        dispatch(analyticsUtils.track.GAEventV2(gaEvent));
                        this.modalClose();
                    } else {
                        return promise.reject();
                    }
                })
                // update currentProperty via getDetails endpoint
                .then(dispatch(PostingActions.property.getDetails(propertyId)))
                .catch(() => {
                    this.setState({ errorAtArchive: true });
                });
        } else {
            dispatch(PostingActions.property.archive(propertyId))
                .then((result) => {
                    if (result.success) {
                        dispatch(PropertiesActions.updateCurrentProperty({ archived: true }));
                        const gaEvent = {
                            ...AnalyticsConstants.GA_EVENTS.ARCHIVE_LISTING,
                            ...(aliasEncoded) && { label: aliasEncoded },
                        };

                        dispatch(analyticsUtils.track.GAEventV2(gaEvent));
                        this.modalClose();
                    } else {
                        return promise.reject();
                    }
                })
                // update currentProperty via getDetails endpoint
                .then(dispatch(PostingActions.property.getDetails(propertyId)))
                .catch(() => {
                    this.setState({ errorAtArchive: true });
                });
        }
    }

    // handle back/cancel button at archive stage
    abortArchiveProcess() {
        this.setState({ isUserAtArchiveStep: false, errorAtArchive: false });
    }

    selectAddress(address) {
        const {
            unit,
            isRoomForRentChecked,
            propertyType,
        } = this.state;

        this.setState({
            address: address.name,
        });

        this.checkLocationRestriction(address.name, unit, isRoomForRentChecked, propertyType);
    }

    autoChoosePropertyType(address, unit, restrictionsResponse) {
        const { propertyType } = this.state;
        const { dispatch } = this.props;

        // if Multifamily, must be either community or unit
        if (!restrictionsResponse.willAppear) {
            const propertyTypeToShow = unit
                ? PropertyConstants.PROPERTY_TYPES.CONDO
                : PropertyConstants.PROPERTY_TYPES.COMMUNITY;
            this.setState({
                propertyType: propertyTypeToShow,
            });
            return;
        }
        if (propertyType) {
            return;
        }
        dispatch(
            AddressActions.getDetails({
                address,
                unit,
            }),
        )
            .then((response) => {
                this.handleAddressDetailsResponse(response);
            });
    }

    handleAddressDetailsResponse(response) {
        const {
            propertyType,
            address,
            isRoomForRentChecked,
            unit,
        } = this.state;

        // only change property type if it hasn't been changed already
        if (!propertyType && !isNil(response.propertyTypeCode)) {
            const propertyTypeToShow = PropertyConstants.PROPERTY_TYPES_BY_CODE[response.propertyTypeCode];
            if (!isNil(propertyTypeToShow)) {
                this.setState({
                    propertyType: propertyTypeToShow,
                });
                this.checkLocationRestriction(address, unit, isRoomForRentChecked, propertyTypeToShow);
            }
        }
    }

    checkLocationRestriction(address, unit, isRoomForRentChecked, propertyType) {
        const { aliasEncoded, dispatch, user } = this.props;
        const { errorObj } = this.state;
        const { brand } = this.context;

        if (!address) {
            return;
        }

        const listingTypeCodes = PropertyConstants.LISTING_CODES_BY_TYPE;

        dispatch(
            AddressActions.getRestrictions({
                address,
                unit,
                listingTypeCode: isRoomForRentChecked ? listingTypeCodes[ROOM] : listingTypeCodes[RENTAL],
                propertyTypeCode: propertyType ? PropertyConstants.PROPERTY_CODES_BY_TYPE[propertyType] : null,
            }),
        )
            .then((response) => {
                const errorMessage = getLocationRestrictionErrorMessage(response, brand, user);

                if (errorMessage) {
                    const gaEvent = {
                        ...AnalyticsConstants.GA_EVENTS.EXISTING_LISTING_POSTED_WARNING,
                        ...(aliasEncoded) && { label: aliasEncoded },
                    };

                    dispatch(analyticsUtils.track.GAEventV2(gaEvent));
                }

                const copyErrorObj = assign({}, errorObj, {
                    address: {
                        message: errorMessage,
                    },
                });

                // update state
                this.setState({
                    errorObj: copyErrorObj,
                    duplicateAccountListingId: response.duplicateAccountListingId,
                });

                this.autoChoosePropertyType(address, unit, response);
            }).catch(error => error);
    }

    updateMFNumUnits(e) {
        this.setState({
            disableSubmit: false,
            numUnits: e.target.value,
        });
    }

    updateDeletePropertyReason(e) {
        const deletePropertyReason = e.target.value;
        const disableDeletePropertySubmit = !deletePropertyReason;
        this.setState({
            disableDeletePropertySubmit,
            deletePropertyReason,
        });
    }

    updateUnit(e) {
        const {
            address,
            isRoomForRentChecked,
            propertyType,
        } = this.state;
        const { target: { value } } = e;

        this.setState({ unit: value });

        // the "error" around valid without unit number is a warning and not a blocker
        // we update the state's errorObj to display the warning
        // we also update the state's errorObj in the restrictions check
        // this created a race condition
        this.validateAddress()
            .catch(error => logger.error(error))
            .finally(() => {
                // always check restrictions
                this.checkLocationRestriction(address, value, isRoomForRentChecked, propertyType);
            });
    }

    toggleRoomForRentChecked() {
        const { address, unit, propertyType } = this.state;
        this.checkLocationRestriction(address, unit, !this.state.isRoomForRentChecked, propertyType);
        this.setState(prevState => ({ isRoomForRentChecked: !prevState.isRoomForRentChecked }));
    }

    updatePropertyType(e) {
        if (e.target.value) {
            this.setState({
                disableSubmit: false,
                propertyType: e.target.value,
            });
            const {
                address,
                isRoomForRentChecked,
                unit,
            } = this.state;
            this.checkLocationRestriction(address, unit, isRoomForRentChecked, e.target.value);
        }
    }

    validateAddress() {
        const { address, unit, errorObj } = this.state;
        const { user, dispatch } = this.props;

        return dispatch(
            AddressActions.verify({
                address,
                unit,
            }),
        )
            .then((response) => {
                const { isValidatedWithoutUnit } = response;

                // copy and clear error obj
                const copyErrorObj = assign({}, errorObj, {
                    address: {
                        message: '',
                    },
                    unitNumber: {
                        message: '',
                    },
                });

                // if the property is valid without a unit number
                if (unit !== '' && isValidatedWithoutUnit) {
                    // inform the user
                    copyErrorObj.unitNumber.message = `We are unable to validate the
                        property address with this unit number. Please double-check before creating the property.`;
                }

                // update state
                this.setState({ errorObj: copyErrorObj });
            })
            .catch((errorRMWModel) => {
                const clientErrMsg = errorRMWModel.message;

                // copy error obj
                const copyErrorObj = assign({}, errorObj, {
                    address: {
                        message: clientErrMsg,
                    },
                });

                // update state
                this.setState({ errorObj: copyErrorObj });

                // dispatch
                appLogger.logError({
                    message: clientErrMsg,
                    context: 'validateAddress',
                    user,
                });

                return promise.reject();
            });
    }

    validateIsEmptyForm() {
        const {
            address,
            propertyType,
            numUnits,
            errorObj,
        } = this.state;

        const copyErrorObj = assign({}, errorObj);
        let foundError = false;

        // address
        if (isEmpty(address)) {
            foundError = true;

            copyErrorObj.address = {
                message: 'Please enter an address',
            };
        }

        // property type
        if (isEmpty(propertyType)) {
            foundError = true;

            copyErrorObj.propertyType = {
                message: 'Please enter a property type',
            };
        }

        // number of units
        if (propertyType === COMMUNITY) {
            if (isEmpty(numUnits)) {
                foundError = true;

                copyErrorObj.numberOfUnits = {
                    message: 'Please enter number of units',
                };
            }
        }

        // update state
        this.setState({ errorObj: copyErrorObj });

        return foundError;
    }

    // limit num Units feild upto 20 chars input
    handleUnitsChange(e) {
        const unit = e.target.value;
        const unitLengthLimit = 20;
        if (unit.length <= unitLengthLimit) {
            this.setState({
                disableSubmit: false,
                unit,
            });
        }
    }

    // isEmpty(errorObj.address.message)
    validateForm() {
        return new Promise((resolve, reject) => {
            // reset errors
            this.setState({ errorObj: defaultErrorObj }, () => {
                // validate
                const isFormEmpty = this.validateIsEmptyForm();

                // check for empty fields
                if (!isFormEmpty) {
                    // validate address
                    this.validateAddress()
                        .then(() => {
                            // success
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                } else {
                    reject();
                }
            });
        });
    }

    handleAutoCompleteBlur = () => {
        const {
            address,
            isRoomForRentChecked,
            keyUpTimeoutForAddress,
            unit,
            propertyType,
        } = this.state;

        clearTimeout(keyUpTimeoutForAddress);
        this.checkLocationRestriction(address, unit, isRoomForRentChecked, propertyType);
    }

    handleAutoCompleteOnKeyUp = () => {
        const {
            address,
            isRoomForRentChecked,
            keyUpTimeoutForAddress,
            unit,
            propertyType,
        } = this.state;

        clearTimeout(keyUpTimeoutForAddress);
        this.setState({
            keyUpTimeoutForAddress: setTimeout(() => {
                this.checkLocationRestriction(address, unit, isRoomForRentChecked, propertyType);
            }, 2000),
        });
    }

    // render add property content
    renderAddPropertyModalContent() {
        const {
            addressList,
            user,
            isArchived,
            currentProperty,
        } = this.props;

        const { isEditable, canBeArchived } = currentProperty;

        const {
            isRoomForRentChecked,
            propertyType,
            address,
            behavior,
            errorObj,
            unit,
            numUnits,
            disableSubmit,
            disableDeletePropertySubmit,
            isAddressEditable,
            mktPath,
        } = this.state;

        const isCommunityType = propertyType === COMMUNITY;
        const isAdmin = userUtils.isPostingPathAdmin(user.roles);
        const roomForRentLabel = 'This is a room for rent with a shared living space';
        const disableSelectionForCommunity = behavior === 'Edit' && isCommunityType;
        const disableSelectionForNonCommunity = behavior === 'Edit' && !isCommunityType;
        const isArchiveButtonDisabled = disableSubmit || (!canBeArchived && !isArchived);
        const isInputFieldDisabled = !isAddressEditable || !isEditable || isArchived;
        const isRoomForRentDisabled = (behavior === 'Edit') ||
                                      isInputFieldDisabled ||
                                      (mktPath === StringConstants.MARKETING_PATH.LEASES);
        const showArchiveRestrictionText = !canBeArchived && !isArchived;
        const toolTip = (
            <Tooltip
                shouldCloseOnOutsideClick={true}
                body={this.renderRoomForRentToolTip()}
            />
        );
        return (
            <div>
                {behavior === 'Add' && (
                    <Text
                        paddingY="md"
                        fontType="sectionheading"
                        as="div"
                    >
                        Please provide your property&apos;s info.
                    </Text>
                )}
                <StyledAutoComplete
                    paddingBottom={1.5}
                    displayItemList={false}
                    errorMessage={errorObj.address.message}
                    label="Property address"
                    inModal={true}
                    itemList={addressList}
                    placeholder=""
                    onBlur={this.handleAutoCompleteBlur}
                    onChange={this.handleAddressChange}
                    onClick={this.selectAddress}
                    onKeyUp={this.handleAutoCompleteOnKeyUp}
                    onTriggerAction={this.handleTriggerAction}
                    value={address}
                    disabled={isInputFieldDisabled}
                />
                {!isAddressEditable && this.renderSupportOrAddNewPropertyError()}
                {!isCommunityType && (
                    <FormField paddingY={1.5}>
                        <Label>Unit number</Label>
                        <Input
                            aria-label="Unit number"
                            placeholder="Unit number"
                            onBlur={this.updateUnit}
                            onChange={this.handleUnitsChange}
                            value={unit}
                            disabled={isInputFieldDisabled}
                        />
                        <FormHelp error={errorObj.unitNumber.message.length > 1}>
                            {errorObj.unitNumber.message}
                        </FormHelp>
                    </FormField>
                )}
                <FormField paddingY={1.5}>
                    <Label hasError={errorObj.propertyType.message.length > 1}>Property type</Label>
                    <StyledSelect
                        aria-label="Property type"
                        value={propertyType || 'Please select'}
                        error={errorObj.propertyType.message.length > 1}
                        onChange={this.updatePropertyType}
                        disabled={isInputFieldDisabled}
                    >
                        <option disabled={true}>Please select</option>
                        <option disabled={disableSelectionForCommunity} value={HOUSE}>
                            {HOUSE}
                        </option>
                        <option disabled={disableSelectionForCommunity} value={CONDO}>
                            {CONDO}
                        </option>
                        <option disabled={disableSelectionForCommunity} value={TOWNHOUSE}>
                            {TOWNHOUSE}
                        </option>
                        <option disabled={disableSelectionForNonCommunity} value={COMMUNITY}>
                            {COMMUNITY}
                        </option>

                    </StyledSelect>
                    <FormHelp error={errorObj.propertyType.message.length > 1}>
                        {errorObj.propertyType.message}
                    </FormHelp>
                </FormField>
                {isCommunityType ? (
                    <FormField paddingTop={1.5} paddingBottom="xs">
                        <Label hasError={errorObj.numberOfUnits.message.length > 1}>Number of units</Label>
                        <Input
                            error={errorObj.numberOfUnits.message.length > 1}
                            onChange={e => this.setState({ numUnits: e.target.value })}
                            onBlur={this.updateMFNumUnits}
                            value={numUnits}
                            disabled={isInputFieldDisabled}
                        />
                        <FormHelp error={errorObj.numberOfUnits.message.length > 1}>
                            {errorObj.numberOfUnits.message}
                        </FormHelp>
                    </FormField>
                ) : (
                    <Spacer paddingY="xs">
                        <StyledCheckboxContainer>
                            <LabeledControl
                                controlId="LabeledControl-roomForRent"
                                label={<Label>{roomForRentLabel}</Label>}
                                control={(
                                    <Checkbox
                                        checked={isRoomForRentChecked}
                                        onChange={this.toggleRoomForRentChecked}
                                    />
                                )}
                                disabled={isRoomForRentDisabled}
                            />
                            {isRoomForRentDisabled && (
                                <Popper
                                    placement="top"
                                    triggered={toolTip}
                                />
                            )}
                        </StyledCheckboxContainer>
                        {isRoomForRentChecked && this.renderRoomForRentNotice()}
                    </Spacer>
                )}
                {behavior === 'Add' ? (
                    this.renderAddBehaviorCTAContainer()
                ) : (
                    <Spacer paddingY="xs">
                        <Button
                            fluid={true}
                            buttonType="primary"
                            onClick={this.handleEditPropertySubmit}
                            disabled={disableSubmit || isInputFieldDisabled}
                        >
                                Save changes
                        </Button>
                        {isAdmin && (
                            <div>
                                <HR />
                                <Text>Or, if you no longer manage this property:</Text>
                                <FormField paddingY={1.5}>
                                    <Label>Reason for deletion</Label>
                                    <Input
                                        placeholder="Reason for deletion"
                                        onBlur={this.updateDeletePropertyReason}
                                    />
                                </FormField>
                                <Spacer paddingY="xs">
                                    <Button
                                        fluid={true}
                                        buttonType="secondary"
                                        onClick={this.handleDeletePropertySubmit}
                                        disabled={disableSubmit || disableDeletePropertySubmit}
                                    >
                                            Delete property
                                    </Button>
                                </Spacer>
                            </div>
                        )}
                        <div>
                            <HR />
                            <Text>
                                {isArchived ?
                                    'If you wish to begin using this property' :
                                    'No longer own or manage this property?'
                                }
                            </Text>
                            <Spacer paddingY="xs">
                                <Button
                                    fluid={true}
                                    buttonType="secondary"
                                    onClick={this.handleArchiveOrUnArchive}
                                    disabled={isArchiveButtonDisabled}
                                >
                                    {isArchived ? 'Unarchive property' : 'Archive property'}
                                </Button>
                                {showArchiveRestrictionText && (
                                    <Text fontType="fineprint">
                                            Properties that are listed for rent or
                                            collecting rent payments cannot be archived.
                                    </Text>
                                )}
                            </Spacer>
                        </div>
                    </Spacer>
                )}
            </div>
        );
    }

    renderRoomForRentNotice() {
        const text = 'Your room for rent listing will not be posted on Zillow. ';
        const linkTest = 'Learn more.';
        const url = 'https://help.zillowrentalmanager.com/hc/en-us/articles/217482388-Why-was-my-listing-Declined';

        return (
            <Text
                key="rr-notice"
                className="RR-Notice"
                fontType="smallbody"
            >
                {text}
                <Link
                    type="secondary"
                    target="_blank"
                    to={url}
                >
                    {linkTest}
                </Link>
            </Text>
        );
    }

    showAddPropertyModal() {
        const { dispatch, user } = this.props;
        const delay = 200;

        if (user.features.canAddListing) {
            dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: false }));

            setTimeout(() => {
                dispatch(ModalActions.updateDisplayAddPropertyModal({
                    isOpen: true,
                    behavior: 'Add',
                }));
            }, delay);
        }
    }

    renderRoomForRentToolTip() {
        const text = `Once a property is marked as room for rent it cannot be changed. If you want to list the entire
        unit for rent, please `;
        const linkTest = 'add a new property.';
        return (
            <Text
                className="RR-ToolTip"
                fontType="body"
            >
                {text}
                <Link
                    type="secondary"
                    onClick={this.handleAddPropertyClick}
                >
                    {linkTest}
                </Link>
            </Text>
        );
    }

    renderSupportOrAddNewPropertyError() {
        let { paths } = this.context;
        const { paths: pPaths, user } = this.props;
        const { features } = user;
        const { canAddListing } = features;

        // There is no context support for unit testing
        // for unit testing set paths via props
        paths = paths || pPaths;

        const addNewPropertyUrl = `${paths.propertiesPage}?postingPath=true&itc=ZRMEditAddressAddNew`;

        return (
            <React.Fragment>
                <FormHelp error={true} marginTop="xs">
                    Please contact
                    <Link
                        reactRouterLink={v4Link}
                        target="_blank"
                        to={paths.helpPage}
                        escape={true}
                    >
                        &nbsp;customer support&nbsp;
                    </Link>
                    if there is an error with the address.
                    {canAddListing && (
                        <React.Fragment>
                            <br />
                            You can also
                            <Link
                                reactRouterLink={v4Link}
                                onClick={() => {
                                    window.router.transitionTo(addNewPropertyUrl);
                                    this.modalClose();
                                }}
                            >
                                &nbsp;add a new property or unit
                            </Link>
                            .
                        </React.Fragment>
                    )}
                </FormHelp>
            </React.Fragment>
        );
    }

    render() {
        const {
            address,
            behavior,
            errorAtArchive,
            isOpen,
            isUserAtArchiveStep,
        } = this.state;
        const { isArchived, currentProperty } = this.props;
        const { isAddressValid } = currentProperty;
        const disableClose = isAddressValid === false;
        const headerText = `${behavior} Property`;

        return (
            <StyledModalDialog
                className={disableClose && 'ModalDialog--disabled'}
                scrollDocument={true}
                shouldCloseOnOutsideClick={isAddressValid}
                shouldCloseOnESCKeyPress={isAddressValid}
                wide={true}
                isOpen={isOpen}
                header={(
                    <Heading level={6}>{headerText}</Heading>
                )}
                id="add-property-modal"
                onClose={() => this.modalClose(false, true)}
            >
                <DialogBody>
                    {!isUserAtArchiveStep ?
                        this.renderAddPropertyModalContent() : (
                            <ArchiveProperty
                                abortArchiveProcess={this.abortArchiveProcess}
                                actionHandler={this.handleArchiveOrUnArchive}
                                address={address}
                                isArchived={isArchived}
                                errorAtArchive={errorAtArchive}
                            />
                        )
                    }
                </DialogBody>
            </StyledModalDialog>
        );
    }
}

const mapStateToProps = state => ({
    addressList: AddressSelectors.makeAddressList(state),
    modalAddProperty: ModalSelectors.makeAddPropertyModal(state),
    user: UserSelectors.makeUser(state),
    currentProperty: PropertiesSelectors.makeCurrentProperty(state),
    search: RoutingSelectors.makeSearch(state),
});

export { AddPropertyModal };
export default connect(mapStateToProps)(AddPropertyModal);
